import * as React from 'react';

import {colors} from 'shells/colors/colors';
import {getEditionStatusInfo} from 'shells/edition/get-edition-status-info';
import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Percentage} from 'shells/percentage/percentage';
import {Table} from 'shells/table/table';
import {
    convertSortByToUrlSortParam,
    convertUrlSortParamToTableSortBy,
} from 'shells/table/table-sort-helpers';
import type {SortBy} from 'shells/table/types';

import type {GetSmsBroadcasts_smsBroadcasts as SmsBroadcast} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';

import {EmptyState} from './empty-state';
import type {EngagementStatsMap} from './graphql/hooks';

const keyMap: Record<string, 'totalSentCount' | 'responseRate' | 'undeliveredRate' | 'optoutRate'> =
    {
        recipients: 'totalSentCount',
        responseRate: 'responseRate',
        undeliverableRate: 'undeliveredRate',
        optOutRate: 'optoutRate',
    };

type Props = {
    isLoading: boolean;
    broadcasts: SmsBroadcast[];
    engagementStatsMap: EngagementStatsMap;
    isLoadingEngagementStats: boolean;
    sortString: string;
    isFiltering: boolean;
    error: string | null | undefined;
    onCreateBroadcast: () => void;
    isCreating: boolean;
    onChangeSort: (newSortString?: string | null | undefined) => void;
};

export function BroadcastsTableView(props: Props) {
    const {engagementStatsMap, isLoadingEngagementStats} = props;

    const {user} = useGetSessionUser();
    const canAccessFullCampaigns = user?.permissions?.canAccessFullCampaigns ?? false;

    const statsSortType = React.useCallback(
        (rowA: any, rowB: any, columnId: string) => {
            const key = keyMap[columnId];
            if (!key) return 0;

            const aStats = engagementStatsMap.get(rowA.values[columnId]);
            const bStats = engagementStatsMap.get(rowB.values[columnId]);
            const a = aStats?.[key] ?? null;
            const b = bStats?.[key] ?? null;

            if (a === null && b === null) return 0;
            if (a === null) return 1;
            if (b === null) return -1;
            return a - b;
        },
        [engagementStatsMap]
    );

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Name',
                accessor: (broadcast: SmsBroadcast) => broadcast.edition,
                dataType: 'linkedName',
            },
            {
                id: 'message',
                Header: 'Message',
                accessor: (broadcast: SmsBroadcast) =>
                    broadcast.edition ? broadcast.edition.content : '',
                dataType: 'string',
                shouldTruncate: true,
                minWidth: 150,
                width: 200,
                maxWidth: 250,
            },
            {
                id: 'sentTime',
                Header: 'Date sent',
                accessor: (broadcast: SmsBroadcast) =>
                    broadcast.edition.scheduledTime
                        ? broadcast.edition.scheduledTime
                        : 'Not yet sent',
                dataType: 'timestamp',
            },
            {
                id: 'modifiedTime',
                Header: 'Last updated',
                accessor: (broadcast: SmsBroadcast) => broadcast.edition.modifiedTime,
                dataType: 'timestamp',
                minWidth: 150,
                width: 200,
                maxWidth: 200,
            },
            {
                id: 'status',
                Header: 'Status',
                accessor: (broadcast: SmsBroadcast) =>
                    getEditionStatusInfo(
                        broadcast.edition.deletedTime ? 'ARCHIVED' : broadcast.edition.status
                    ),
                dataType: 'statusBadge',
            },
            ...(canAccessFullCampaigns
                ? [
                      {
                          id: 'owner',
                          Header: 'Owner',
                          accessor: (broadcast: SmsBroadcast) =>
                              broadcast.owner ? broadcast.owner.name : '',
                          dataType: 'string',
                          shouldTruncate: true,
                          minWidth: 100,
                          width: 150,
                          maxWidth: 200,
                      },
                  ]
                : []),
            {
                id: 'recipients',
                Header: 'Recipients',
                accessor: (broadcast: SmsBroadcast) => broadcast.id,
                dataType: 'component',
                sortType: statsSortType,
                minWidth: 90,
                width: 90,
                maxWidth: 110,
                Cell: ({cell}: {cell: {value: string}}) => {
                    const stats = engagementStatsMap.get(cell.value);
                    if (isLoadingEngagementStats) {
                        return <LoadingPage size={16} padding={0} fill={colors.greyLt} />;
                    }
                    if (!stats || stats.totalSentCount === 0) {
                        return <span>—</span>;
                    }

                    return <span>{stats.totalSentCount.toLocaleString()}</span>;
                },
            },
            {
                id: 'responseRate',
                Header: 'Response rate',
                accessor: (broadcast: SmsBroadcast) => broadcast.id,
                dataType: 'component',
                sortType: statsSortType,
                minWidth: 130,
                width: 130,
                maxWidth: 150,
                Cell: ({cell}: {cell: {value: string}}) => {
                    const stats = engagementStatsMap.get(cell.value);
                    if (isLoadingEngagementStats) {
                        return <LoadingPage size={16} padding={0} fill={colors.greyLt} />;
                    }

                    if (!stats) return <span>—</span>;

                    return <span>{Percentage({value: stats.responseRate, precision: 2})}</span>;
                },
            },
            {
                id: 'undeliverableRate',
                Header: 'Undeliverable rate',
                accessor: (broadcast: SmsBroadcast) => broadcast.id,
                dataType: 'component',
                sortType: statsSortType,
                minWidth: 130,
                width: 130,
                maxWidth: 150,
                Cell: ({cell}: {cell: {value: string}}) => {
                    const stats = engagementStatsMap.get(cell.value);
                    if (isLoadingEngagementStats) {
                        return <LoadingPage size={16} padding={0} fill={colors.greyLt} />;
                    }
                    if (!stats) return <span>—</span>;

                    return <span>{Percentage({value: stats.undeliveredRate, precision: 2})}</span>;
                },
            },
            {
                id: 'optOutRate',
                Header: 'Opt-out rate',
                accessor: (broadcast: SmsBroadcast) => broadcast.id,
                dataType: 'component',
                sortType: statsSortType,
                minWidth: 100,
                width: 100,
                maxWidth: 150,
                hasRightBorder: true,
                Cell: ({cell}: {cell: {value: string}}) => {
                    const stats = engagementStatsMap.get(cell.value);
                    if (isLoadingEngagementStats) {
                        return <LoadingPage size={16} padding={0} fill={colors.greyLt} />;
                    }
                    if (!stats) return <span>—</span>;

                    return <span>{Percentage({value: stats.optoutRate, precision: 2})}</span>;
                },
            },
        ],
        [canAccessFullCampaigns, engagementStatsMap, isLoadingEngagementStats, statsSortType]
    );

    const handleSortByChange = (newSortBy: SortBy[]) => {
        const newSortString = convertSortByToUrlSortParam(newSortBy);

        props.onChangeSort(newSortString);
    };

    return (
        <div className='block max-full-width overflow-auto full-height pos-relative br-3 '>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderCellBorders={true}
                hasHoverState={false}
                isCompact={true}
                stickyHeaders={true}
                hasBorders={true}
                isCheckboxSelectable={false}
                clickingSelectsRow={false}
                disableFilters={true}
                disableSorting={false}
                // Removes "triple sort" and forces there
                // always to be a asc/desc sort applied
                disableSortRemove={true}
                sortBy={convertUrlSortParamToTableSortBy(props.sortString)}
                onChangeSortBy={handleSortByChange}
                shouldPreventSelectAll={false}
                // @ts-expect-error
                columns={columns}
                data={props.broadcasts}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.error),
                        component: <ErrorState errorMessage={props.error} />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage padding={0} />,
                    },
                    {
                        shouldRender: () =>
                            !props.broadcasts.length && props.isFiltering && !props.isLoading,
                        component: (
                            <NothingFoundEmptyState
                                title='No broadcasts were found'
                                subtitle='Try loosening your search!'
                            />
                        ),
                    },
                    {
                        shouldRender: () => !props.broadcasts.length && !props.isLoading,
                        component: (
                            <EmptyState
                                onPrimaryCtaClick={() => props.onCreateBroadcast()}
                                isCreating={props.isCreating}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}
